import React, { useState } from 'react';
import { Upload, Shield, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LZString from 'lz-string';

interface VerificationUploadProps {
  type: 'id' | 'address';
  onClose: () => void;
}

const VerificationUpload: React.FC<VerificationUploadProps> = ({ onClose, type }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState(type === 'id' ? 'drivers_license' : 'utility_bill');
  const [isUploading, setIsUploading] = useState(false);
  const { user, updateUserProfile } = useAuth();

  const documentTypes = type === 'id' 
    ? [
        { value: 'drivers_license', label: "Driver's License" },
        { value: 'passport', label: 'Passport' },
        { value: 'state_id', label: 'State ID' }
      ]
    : [
        { value: 'utility_bill', label: 'Utility Bill' },
        { value: 'bank_statement', label: 'Bank Statement' },
        { value: 'lease_agreement', label: 'Lease Agreement' }
      ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !user) return;

    setIsUploading(true);
    try {
      // Convert file to base64 and compress
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const compressed = LZString.compressToUTF16(base64String);

        const verifications = {
          ...user.verifications,
          [type]: {
            status: 'pending',
            type: documentType,
            uploadDate: new Date().toISOString(),
            documentUrl: compressed // Store compressed document data
          }
        };

        await updateUserProfile({
          ...user,
          verifications
        });

        onClose();
      };
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold">
                {type === 'id' ? 'Verify Your Identity' : 'Verify Your Address'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
              <div className="text-sm text-yellow-700">
                <p className="font-medium mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Document must be valid and not expired</li>
                  <li>All information must be clearly visible</li>
                  <li>File size must be under 5MB</li>
                  <li>Supported formats: JPG, PNG, PDF</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                {documentTypes.map(doc => (
                  <option key={doc.value} value={doc.value}>
                    {doc.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Document
              </label>
              <div 
                onClick={() => document.getElementById('file-upload')?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-500 transition-colors"
              >
                {selectedFile ? (
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-green-600 font-medium">{selectedFile.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-gray-600">Click to upload or drag and drop</p>
                    <p className="text-sm text-gray-500">JPG, PNG, or PDF up to 5MB</p>
                  </div>
                )}
                <input
                  id="file-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size <= 5 * 1024 * 1024) {
                      setSelectedFile(file);
                    } else {
                      alert('File size must be less than 5MB');
                    }
                  }}
                  className="hidden"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!selectedFile || isUploading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4" />
                    <span>Submit for Verification</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerificationUpload;